// ---------------------------------------------------------
//  Halka Screen v2.0 — Multi-circle management
// ---------------------------------------------------------

import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";
import {
  Users, Hash, Plus, Crown, Shield, User,
  ArrowLeft, Copy, Check, BookOpen,
  ChevronRight, QrCode, LogOut,
} from "lucide-react";

// -- Types ------------------------------------------------

interface MyGroup {
  groupId: string;
  groupName: string;
  role: string;
  memberCount?: number;
}

interface AvailableGroup {
  id: string;
  name: string;
  description?: string;
  memberCount: number;
  maxMembers: number;
}

interface HalkaScreenProps {
  myGroups: MyGroup[];
  availableGroups: AvailableGroup[];
  userId: string;
  onJoinGroup: (groupId: string, password: string) => Promise<void>;
  onCreateGroup: (name: string, password: string, scoreboardVisible: boolean) => Promise<void>;
  onLeaveGroup: (groupId: string) => Promise<void>;
  onSelectGroup: (groupId: string) => void;
  onOpenHatim: (groupId: string) => void;
}

// -- Role helpers -----------------------------------------

const ROLE_CONFIG: Record<string, { label: string; icon: typeof Crown; color: string; borderClass: string }> = {
  admin:     { label: "Kurucu",   icon: Crown,  color: "text-gold",             borderClass: "border-gold/60" },
  moderator: { label: "Moderator", icon: Shield, color: "text-ehille-blue",     borderClass: "border-ehille-blue/40" },
  member:    { label: "Uye",      icon: User,   color: "text-muted-foreground", borderClass: "border-border" },
};

function getRoleConfig(role: string) {
  return ROLE_CONFIG[role] ?? ROLE_CONFIG.member;
}

// -- Component --------------------------------------------

export function HalkaScreen({
  myGroups, availableGroups, userId: _userId,
  onJoinGroup, onCreateGroup, onLeaveGroup, onSelectGroup, onOpenHatim,
}: HalkaScreenProps) {
  type View = "main" | "join" | "create" | "detail";

  const [view, setView] = useState<View>("main");
  const [selectedGroup, setSelectedGroup] = useState<MyGroup | null>(null);

  // Join form
  const [joinCode, setJoinCode] = useState("");
  const [joinPassword, setJoinPassword] = useState("");

  // Create form
  const [newName, setNewName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [scoreboardVisible, setScoreboardVisible] = useState(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // -- Helpers --

  const resetForms = () => {
    setJoinCode(""); setJoinPassword("");
    setNewName(""); setNewPassword(""); setScoreboardVisible(true);
    setError("");
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // -- Join handler --

  const handleJoin = async () => {
    if (!joinCode || !joinPassword) return;
    setLoading(true); setError("");
    try {
      await onJoinGroup(joinCode, joinPassword);
      resetForms();
      setView("main");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Hata olustu");
    } finally {
      setLoading(false);
    }
  };

  // -- Create handler --

  const handleCreate = async () => {
    if (!newName || !newPassword) return;
    setLoading(true); setError("");
    try {
      await onCreateGroup(newName, newPassword, scoreboardVisible);
      resetForms();
      setView("main");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Hata olustu");
    } finally {
      setLoading(false);
    }
  };

  // -- Leave handler --

  const handleLeave = async (groupId: string) => {
    setLoading(true); setError("");
    try {
      await onLeaveGroup(groupId);
      setSelectedGroup(null);
      setView("main");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Hata olustu");
    } finally {
      setLoading(false);
    }
  };

  // -- Back button component --

  const BackButton = ({ target = "main" as View }: { target?: View }) => (
    <button
      onClick={() => { setView(target); setError(""); }}
      className="flex items-center gap-2 text-muted-foreground mb-6"
    >
      <ArrowLeft size={20} />
      <span className="text-sm font-medium">{t("back")}</span>
    </button>
  );

  // ========================================================
  //  JOIN VIEW
  // ========================================================
  if (view === "join") {
    return (
      <div className="pb-24 px-4 pt-6">
        <BackButton />

        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gold/10 flex items-center justify-center mx-auto mb-4">
            <Hash className="text-gold" size={28} />
          </div>
          <h2 className="text-xl font-bold">{t("join_with_code")}</h2>
        </div>

        <div className="space-y-4">
          <Input
            label={t("circle_code")}
            value={joinCode}
            onChange={e => setJoinCode(e.target.value)}
            placeholder="Halka kodunu girin"
          />
          <Input
            label={t("group_password")}
            type="password"
            value={joinPassword}
            onChange={e => setJoinPassword(e.target.value)}
            placeholder="Sifre"
          />

          {error && <p className="text-ehille-red text-sm text-center">{error}</p>}

          {/* Available groups list */}
          {availableGroups.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Mevcut Halkalar
              </h3>
              {availableGroups.map(g => (
                <Card
                  key={g.id}
                  className={cn(
                    "cursor-pointer transition-all",
                    joinCode === g.id ? "border-gold/50 bg-gold/5" : "hover:border-gold/30",
                  )}
                  onClick={() => setJoinCode(g.id)}
                >
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center">
                      <Users className="text-gold" size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{g.name}</p>
                      {g.description && (
                        <p className="text-[10px] text-muted-foreground truncate">{g.description}</p>
                      )}
                      <p className="text-[10px] text-muted-foreground">
                        {g.memberCount}/{g.maxMembers} uye
                      </p>
                    </div>
                    {joinCode === g.id && <Check size={18} className="text-gold shrink-0" />}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <Button
            onClick={handleJoin}
            size="lg"
            className="w-full"
            disabled={loading || !joinCode || !joinPassword}
          >
            {loading ? t("joining") : t("join_group")}
          </Button>
        </div>
      </div>
    );
  }

  // ========================================================
  //  CREATE VIEW
  // ========================================================
  if (view === "create") {
    return (
      <div className="pb-24 px-4 pt-6">
        <BackButton />

        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gold/10 flex items-center justify-center mx-auto mb-4">
            <Plus className="text-gold" size={28} />
          </div>
          <h2 className="text-xl font-bold">{t("create_group")}</h2>
        </div>

        <div className="space-y-4">
          <Input
            label={t("group_name")}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder={t("group_name_placeholder")}
          />
          <Input
            label={t("group_password")}
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder={t("password_hint")}
          />

          {/* Scoreboard visibility toggle */}
          <div className="flex items-center justify-between p-3 rounded-xl border border-border">
            <div>
              <p className="text-sm font-medium">Skor Tablosu Gorunsun</p>
              <p className="text-[10px] text-muted-foreground">Uyeler birbirinin puanini gorsun</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={scoreboardVisible}
              onClick={() => setScoreboardVisible(v => !v)}
              className={cn(
                "relative w-11 h-6 rounded-full transition-colors",
                scoreboardVisible ? "bg-gold" : "bg-muted",
              )}
            >
              <span
                className={cn(
                  "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform",
                  scoreboardVisible && "translate-x-5",
                )}
              />
            </button>
          </div>

          {error && <p className="text-ehille-red text-sm text-center">{error}</p>}

          <Button
            onClick={handleCreate}
            size="lg"
            className="w-full"
            disabled={loading || !newName || !newPassword}
          >
            {loading ? t("creating") : t("create_group")}
          </Button>
        </div>
      </div>
    );
  }

  // ========================================================
  //  GROUP DETAIL VIEW
  // ========================================================
  if (view === "detail" && selectedGroup) {
    const rc = getRoleConfig(selectedGroup.role);
    const RoleIcon = rc.icon;

    return (
      <div className="pb-24 px-4 pt-6">
        <BackButton />

        {/* Group header card */}
        <Card className={cn("mb-4 overflow-hidden border-2", rc.borderClass)}>
          <div className="bg-gradient-to-r from-ehille-dark to-ehille-dark2 p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gold/20 flex items-center justify-center">
                <Users className="text-gold" size={26} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-white truncate">{selectedGroup.groupName}</h2>
                <div className="flex items-center gap-1.5 mt-1">
                  <RoleIcon size={12} className={rc.color} />
                  <span className={cn("text-xs font-medium", rc.color)}>{rc.label}</span>
                </div>
              </div>
            </div>
          </div>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center">
                <p className="text-lg font-bold text-gold">{selectedGroup.memberCount ?? "—"}</p>
                <p className="text-[10px] text-muted-foreground">{t("circle_members")}</p>
              </div>
              <div className="text-center">
                <Badge variant="outline" className="text-xs">
                  <RoleIcon size={10} className="mr-1" /> {rc.label}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Share code */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              {t("circle_code")}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-muted rounded-lg px-3 py-2 text-sm font-mono truncate">
                {selectedGroup.groupId}
              </code>
              <Button size="icon" variant="outline" onClick={() => copyCode(selectedGroup.groupId)}>
                {copied ? <Check size={16} className="text-ehille-green" /> : <Copy size={16} />}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              Bu kodu paylasarak arkadaslarinizi davet edin
            </p>
          </CardContent>
        </Card>

        {/* QR Code placeholder */}
        <Card className="mb-4">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
              <QrCode size={20} className="text-muted-foreground" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">QR Kod ile Davet</p>
              <p className="text-[10px] text-muted-foreground">QR kodu taratarak katilim saglayabilirsiniz</p>
            </div>
            <ChevronRight size={16} className="text-muted-foreground" />
          </CardContent>
        </Card>

        {/* Hatim button */}
        <Button
          size="lg"
          className="w-full gap-2 mb-3"
          onClick={() => onOpenHatim(selectedGroup.groupId)}
        >
          <BookOpen size={18} /> Hatim
        </Button>

        {/* Select as active group */}
        <Button
          size="lg"
          variant="outline"
          className="w-full gap-2 mb-3"
          onClick={() => onSelectGroup(selectedGroup.groupId)}
        >
          Aktif Halka Yap
        </Button>

        {/* Leave group */}
        <Button
          variant="outline"
          className="w-full text-ehille-red border-ehille-red/30 hover:bg-ehille-red/10 gap-2"
          disabled={loading}
          onClick={() => handleLeave(selectedGroup.groupId)}
        >
          <LogOut size={16} /> {t("leave_circle")}
        </Button>

        {error && <p className="text-ehille-red text-sm text-center mt-3">{error}</p>}
      </div>
    );
  }

  // ========================================================
  //  MAIN VIEW — My groups list
  // ========================================================
  return (
    <div className="pb-24 px-4 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold">Halkalarim</h1>
          {myGroups.length > 0 && (
            <Badge variant="secondary" className="text-xs h-5 px-1.5">
              {myGroups.length}
            </Badge>
          )}
        </div>
      </div>

      {/* My groups cards */}
      {myGroups.length > 0 ? (
        <div className="space-y-3 mb-6">
          {myGroups.map(g => {
            const rc = getRoleConfig(g.role);
            const RoleIcon = rc.icon;

            return (
              <Card
                key={g.groupId}
                className={cn(
                  "cursor-pointer transition-all hover:shadow-md border-2",
                  rc.borderClass,
                )}
                onClick={() => {
                  setSelectedGroup(g);
                  setView("detail");
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className={cn(
                      "w-12 h-12 rounded-2xl flex items-center justify-center",
                      g.role === "admin" ? "bg-gold/15" : "bg-muted",
                    )}>
                      <Users size={22} className={g.role === "admin" ? "text-gold" : "text-muted-foreground"} />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate">{g.groupName}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge
                          variant={g.role === "admin" ? "warning" : "outline"}
                          className="text-[9px] h-4 px-1.5 gap-0.5"
                        >
                          <RoleIcon size={8} /> {rc.label}
                        </Badge>
                        {g.memberCount != null && (
                          <span className="text-[10px] text-muted-foreground">
                            {g.memberCount} uye
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Hatim shortcut */}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 px-2 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenHatim(g.groupId);
                      }}
                    >
                      <BookOpen size={14} className="mr-1" /> Hatim
                    </Button>

                    <ChevronRight size={16} className="text-muted-foreground shrink-0" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* Empty state */
        <div className="text-center py-10 mb-6">
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Users size={32} className="text-muted-foreground" />
          </div>
          <p className="text-muted-foreground text-sm">{t("no_circle")}</p>
          <p className="text-[10px] text-muted-foreground mt-1">
            Bir halkaya katilarak hedeflerinizi birlikte takip edin
          </p>
        </div>
      )}

      {/* Action buttons — always visible */}
      <div className="space-y-3">
        <Button
          size="lg"
          className="w-full gap-2"
          onClick={() => { resetForms(); setView("join"); }}
        >
          <Hash size={18} /> {t("join_with_code")}
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="w-full gap-2"
          onClick={() => { resetForms(); setView("create"); }}
        >
          <Plus size={18} /> {t("create_group")}
        </Button>
      </div>
    </div>
  );
}
